import sdk from "./sdk/index";
import store from "@/state/store";
import { event } from "vue-analytics";

sdk.eventBus.$on("token-invalid", function() {
  store.commit("auth/SET_CURRENT_USER", {});
  store.commit("auth/SET_USER_TOKEN", null);
});

class Api {
  get rememberToken() {
    return sdk.rememberToken;
  }

  set rememberToken(remember) {
    sdk.rememberToken = remember;
  }

  get userToken() {
    return sdk.token;
  }

  set userToken(token) {
    sdk.token = token;
  }

  /** Overide base methods **/

  /** Authentication API */
  /**
   *
   * @param {Object} data
   * @param {String} data.email
   * @param {String} data.password
   */
  logIn(data) {
    event("api", "log-in");
    return sdk.logIn(data);
  }

  logOut() {
    event("api", "log-out");
    return sdk.logOut();
  }

  getMe() {
    event("api", "get-me");
    return sdk.request({
      url: "/me"
    });
  }

  refreshToken() {
    event("api", "refresh-token");
    return sdk.refreshToken();
  }

  validateToken() {
    event("api", "check-token");
    return sdk.validateToken();
  }

  changePassword(data) {
    event("api", "change-password");
    return sdk.request({
      method: "post",
      url: "/me/password",
      data
    });
  }

  confirmAccount(confirmCode) {
    event("api", "confirm-account");
    return sdk.request({
      url: `/account/confirm/${confirmCode}`
    });
  }

  forgotPassword(data) {
    event("api", "forgot-password");
    return sdk.request({
      method: "post",
      url: "/password/email",
      data
    });
  }

  getResetPasswordFormData(token) {
    event("api", "get-reset-password-form-data");
    return sdk.request({
      url: `/password/reset/${token}`
    });
  }

  resetPassword(data) {
    event("api", "reset-password");
    return sdk.request({
      method: "post",
      url: "/password/reset",
      data
    });
  }

  /** Api methods **/
  getUsers(params) {
    return sdk.request({ method: "get", url: `/users`, params });
  }

  getCitizenUsers(params) {
    return sdk.request({ method: "get", url: `/citizen/users`, params });
  }

  getStaffUsers(params) {
    return sdk.request({ method: "get", url: `/staff/users`, params });
  }

  getHeaderUsers(params) {
    return sdk.request({ method: "get", url: `/header/users`, params });
  }

  deleteUser(id) {
    return sdk.request({ method: "delete", url: `/users/${id}` });
  }
  approveUser(userId) {
    return sdk.request({ method: "get", url: `/approve/${userId}` });
  }
  statisticUser() {
    return sdk.request({ method: "get", url: `/statistic/users` });
  }

  getNotifications() {
    return sdk.request({ method: "get", url: `/notifications` });
  }

  markAsRead(id) {
    return sdk.request({ method: "put", url: `/notifications/${id}/read` });
  }

  getComplaint(id) {
    return sdk.request({ url: `/complaints/${id}` });
  }

  getComplaints(params) {
    return sdk.request({ method: "get", url: `/complaints`, params });
  }

  addComplaintComment(id, data) {
    return sdk.request({
      method: "post",
      url: `/complaints/${id}/comment`,
      data
    });
  }

  assignComplaint(id, data) {
    return sdk.request({
      method: "post",
      url: `/complaints/${id}/assign`,
      data
    });
  }

  getComplaintStatuses(params) {
    return sdk.request({ method: "get", url: `/complaintstatuses`, params });
  }

  getPayment(params) {
    return sdk.request({ method: "get", url: `/paymentdetails`, params });
  }

  searchComplaint(id) {
    return sdk.request({ method: "get", url: `/complaints/${id}/status` });
  }

  getCategories(params) {
    return sdk.request({ method: "get", url: `/categories`, params });
  }

  getCategoryCatalog() {
    return sdk.request({ method: "get", url: `/categories/catalog` });
  }

  updateCategories(data) {
    return sdk.request({ method: "put", url: `/categories/${data.id}`, data });
  }

  addCategories(data) {
    return sdk.request({ method: "post", url: `/categories`, data });
  }

  deleteCategory(id) {
    return sdk.request({ method: "delete", url: `/categories/${id}` });
  }

  getDepartments(params) {
    return sdk.request({ method: "get", url: `/departments`, params });
  }

  addDepartments(data) {
    return sdk.request({ method: "post", url: `/departments`, data });
  }

  updateDepartments(id, data) {
    return sdk.request({ method: "put", url: `/departments/${id}`, data });
  }

  updateExistingUserDepartments(departmentId, id, data) {
    return sdk.request({
      method: "put",
      url: `users/${departmentId}/departments/${id}`,
      data
    });
  }

  updatePassword(id, data) {
    return sdk.request({
      method: "put",
      url: `/users/${id}/updatePassword`,
      data
    });
  }

  uploadImage(data) {
    return sdk.request({
      method: "post",
      url: `/storage/upload/image`,
      data,
      headers: { "Content-Type": "multipart/form-data" }
    });
  }

  addCommentSg(data) {
    return sdk.request({ method: "post", url: `/suggestions`, data });
  }

  deleteCommentSg(id) {
    return sdk.request({ method: "delete", url: `/suggestions/${id}` });
  }

  getLayers(params) {
    return sdk.request({ method: "get", url: `/layers`, params });
  }

  addLayer(data) {
    return sdk.request({ method: "post", url: `/layer`, data });
  }

  updateLayer(id, data) {
    return sdk.request({ method: "post", url: `/layer/${id}`, data });
  }

  deleteLayer(id) {
    return sdk.request({ method: "delete", url: `/layer/${id}` });
  }

  allLayer(param) {
    return sdk.request({ method: "get", url: `/layer/all`, param });
  }

  async getComplexCategories() {
    const res = await sdk.request({
      method: "get",
      url: "/complex-categories"
    });

    return res.data;
  }

  async getComplexCategory(id) {
    const res = await sdk.request({
      method: "get",
      url: `/complex-categories/${id}`
    });
    return res.data;
  }

  async getRoles() {
    const res = await sdk.request({ method: "get", url: `/roles` });
    return res.data;
  }

  async getPermissions() {
    const res = await sdk.request({ method: "get", url: `/permissions` });
    return res.data;
  }

  async changePermission(role, permission) {
    const res = await sdk.request({
      method: "post",
      url: `/roles/${role}/permissions/${permission}`
    });

    return res.data;
  }

  async deletePermission(role, permission) {
    const res = await sdk.request({
      method: "delete",
      url: `/roles/${role}/permissions/${permission}`
    });
    return res.data;
  }

  async getUserByDepartment(id) {
    const res = await sdk.request({
      method: "get",
      url: `/department/users/${id}`
    });

    return res.data;
  }

  async getUserByWard(id) {
    const res = await sdk.request({ method: "get", url: `/ward/users/${id}` });
    return res.data;
  }

  async getWardByDepartment(id) {
    const res = await sdk.request({
      method: "get",
      url: `/department/${id}/wards`
    });

    return res.data;
  }

  addComplaintNature(data) {
    return sdk.request({
      method: "post",
      url: "/complaint-natures",
      data
    });
  }

  updateComplaintNature(id, data) {
    return sdk.request({
      method: "put",
      url: "/complaint-natures/" + id,
      data
    });
  }

  deleteComplaintNature(id) {
    return sdk.request({
      method: "delete",
      url: `/complaint-natures/${id}`
    });
  }

  deleteDepartment(id) {
    return sdk.request({
      method: "delete",
      url: `/departments/${id}`
    });
  }
}

export default Api;
